import React from 'react';
import { Group, ChevronDown, ChevronRight, Edit, Trash2, Link, Plus } from 'lucide-react';
import AdvisorsTable from './AdvisorsTable';

const GroupsTab = ({
  groups,
  loading,
  expandedGroups,
  toggleGroupExpansion,
  groupAdvisors,
  loadingAdvisors,
  handleEditGroup,
  handleDeleteGroup,
  deleteAdivsorFromGroup,
  setAssignFormData,
  setShowAssignModal,
  setShowCustomerAssignModal,
  setCustomerAssignFormData
}) => {
  if (loading && groups.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[980px]">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Pool Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customers
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Advisors
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {groups.map((group, index) => (
            <React.Fragment key={`group-${group.id}-${index}`}>
              <tr className={`hover:bg-gray-50 ${expandedGroups[group.id] ? 'bg-gray-50' : ''}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Group className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{group.group_name}</div>
                      <div className="text-sm text-gray-500">ID: {group.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {group.description || 'No description'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {group.customer_count || 0} customers
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    <span>{group.advisor_count || 0} advisors</span>
                    <button
                      onClick={() => toggleGroupExpansion(group.id)}
                      className="ml-2 text-primary hover:text-primaryDark focus:outline-none"
                      aria-label={expandedGroups[group.id] ? "Collapse advisor list" : "Expand advisor list"}
                    >
                      {expandedGroups[group.id] ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => handleEditGroup(group)}
                      className="text-primary hover:text-primaryDark p-1 rounded"
                      title="Edit Group"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      className="text-red-600 hover:text-red-900 p-1 rounded"
                      title="Delete Group"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className='ml-auto flex flex-wrap gap-2 justify-end'>
                      <button
                        onClick={() => {
                          setAssignFormData(prev => ({ ...prev, group_id: group.id }));
                          setShowAssignModal(true);
                        }}
                        className="bg-primary hover:bg-primaryDark text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap"
                      >
                        <Link className="w-4 h-4" />
                        Assign Advisor
                      </button>
                      <button
                        onClick={() => {
                          setCustomerAssignFormData(prev => ({ ...prev, group_id: group.id }));
                          setShowCustomerAssignModal(true);
                        }}
                        className="bg-primary hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors whitespace-nowrap"
                      >
                        <Plus className="w-4 h-4" />
                        Add Customer
                      </button>
                    </div>
                  </div>
                </td>
              </tr>

              {expandedGroups[group.id] && (
                <tr>
                  <td colSpan="5" className="px-0 py-0 border-t-0">
                    <AdvisorsTable 
                      groupName={group.group_name}
                      advisors={groupAdvisors[group.id] || []}
                      loading={loadingAdvisors[group.id]}
                      deleteAdivsorFromGroup={deleteAdivsorFromGroup}
                      groupId={group.id}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>

      {groups.length === 0 && (
        <div className="text-center py-12">
          <Group className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No groups found</h3>
          <p className="text-gray-500">Try adjusting your search criteria or create a new group.</p>
        </div>
      )}
    </div>
  );
};

export default GroupsTab;